import React, { useState, useEffect } from 'react';
import { Search, ChevronLeft, ArrowRight, Plus, Edit2, Trash2 } from 'lucide-react';
import { FamilyMember } from '@/types';
import FamilyTreeDownloader from '../auth/register/components/download-tree';

interface FamilyNavigatorProps {
  members: FamilyMember[];
  isAdmin?: boolean;
  onEditNode?: (member: FamilyMember) => void;
  onDeleteNode?: (member: FamilyMember) => void;
  onAddChild?: (member: FamilyMember) => void;
}
const FamilyNavigator = ({members, onAddChild, onDeleteNode, onEditNode, isAdmin}: FamilyNavigatorProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentMember, setCurrentMember] = useState(null);
  const [history, setHistory] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);

  const memberMap = {};
    members.forEach(member => {
    memberMap[member.id] = {
      ...member,
      children: members.filter(m => m.parentId === member.id)
    };
  });

  const findPathToMember = (memberId) => {
    const path = [];
    let currentId = memberId;
    
    while (currentId) {
      const member = memberMap[currentId];
      if (!member) break;
      path.unshift(member);
      currentId = member.parent_id;
    }
    
    return path;
  };

  const navigateToMember = (member, pathToMember = null) => {
    const path = pathToMember || findPathToMember(member.id);
    if (path.length > 1) {
      const parentMember = path[path.length - 2];
      setHistory(path.slice(0, -2).reverse());
      setCurrentMember(parentMember);
    } else {
      setHistory([]);
      setCurrentMember(null);
    }
    setShowSearchResults(false);
  };

  const prepareMembers = (members) => {
    return members.reduce((acc, member) => {
      acc.push({
        id: member.id,
        name: member.name,
        parent_id: member.parentId,
        is_root: member.isRoot ? 1 : 0,
        children: member.children
      });
      return acc;
    }, []);
   };

  const navigateToLevel = (member) => {
    const path = findPathToMember(member.id);
    setHistory(path.slice(0, -1).reverse());
    setCurrentMember(member);
    setShowSearchResults(false);
  };

  const getFullName = (member) => {
    const ancestry = [];
    let current = member;
    
    while (current) {
      ancestry.push(current.name);
      current = members.find(m => m.id === current.parentId);
    }
    
    return ancestry.join(' بن ');
  };

 
  useEffect(() => {
    if (searchTerm.trim()) {
      const searchParts = searchTerm.trim();
      
      const results = members.filter(member => {
        const fullName = getFullName(member);
        return fullName.startsWith(searchParts);
      });
      
      setSearchResults(results);
      setShowSearchResults(true);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
   }, [searchTerm, members]);


  const rootMembers = members.filter(m => m.isRoot);

  const handleMemberClick = (member) => {
    setHistory(prev => [...prev, currentMember]);
    setCurrentMember(member);
  };

  const handleBack = () => {
    const newHistory = [...history];
    const previousMember = newHistory.pop();
    setHistory(newHistory);
    setCurrentMember(previousMember);
  };

  const getChildren = (parentId) => {
    return members.filter(member => member.parentId === parentId);
  };

  const getCurrentPath = () => {
    if (!currentMember) return [];
    return findPathToMember(currentMember.id);
  };

  const renderBreadcrumbs = () => {
    const fullPath = [];
    let current = currentMember;
    
    while (current) {
      fullPath.unshift(current);
      current = members.find(m => m.id === current.parentId);
    }

    return (
      <div dir="rtl" className="flex items-center gap-2 mb-4 text-sm overflow-x-auto">
        {fullPath.map((member, index) => (
          <React.Fragment key={member.id}>
            {index > 0 && <ChevronLeft size={16} className="text-gray-400" />}
            <button
              onClick={() => navigateToLevel(member)}
              className="whitespace-nowrap text-primary hover:text-primary/80 hover:underline"
            >
              {member.name}
            </button>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderSearchResults = () => {
    if (!showSearchResults || searchResults.length === 0) return null;
   
    return (
      <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
        {searchResults.map(member => {
          return (
            <button
              key={member.id}
              onClick={() => {
                navigateToLevel(member);
                setSearchTerm('');
              }}
              className="w-full p-3 text-right hover:bg-gray-50 border-b last:border-b-0"
            >
              <div className="font-medium text-gray-800">{getFullName(member)}</div>
            </button>
          );
        })}
      </div>
    );
   };

  const renderMemberList = (members) => (
    <div className="space-y-2" dir="rtl">
      {members.map(member => {
        const children = getChildren(member.id);
        return (
          <div key={member.id} className="relative group">
            <div className="w-full p-4 text-right bg-white hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors">
              <div className="flex items-center justify-between">
                
              <div className="flex flex-col items-start">
                  {children.length > 0 ? (
                    <button onClick={() => handleMemberClick(member)} className="text-lg font-medium text-gray-800 hover:text-primary">
                      {member.name}
                    </button>
                  ) : (
                    <span className="text-lg font-medium text-gray-800">{member.name}</span>
                  )}
                  {children.length > 0 && (
                    <button onClick={() => handleMemberClick(member)} className="text-lg font-medium text-gray-800 hover:text-primary">
                    <span className="text-sm text-gray-500">
                       عدد الأبناء {children.length}
                    </span>
                    </button>
                  )}
                </div>

                {isAdmin && (
                  <div className="flex gap-4">
                    <button onClick={() => onAddChild(member)} className="p-1 text-gray-500 hover:text-primary">
                      <Plus size={18} />
                    </button>
                    <button onClick={() => onEditNode(member)} className="p-1 text-gray-500 hover:text-primary">
                      <Edit2 size={18} />
                    </button>
                    <button onClick={() => onDeleteNode(member)} className="p-1 text-gray-500 hover:text-red-600">
                      <Trash2 size={18} />
                    </button>
                  </div>
                )}
                
                {children.length > 0 && <button onClick={() => handleMemberClick(member)} className="text-lg font-medium text-gray-800 hover:text-primary">
<ChevronLeft className="text-gray-400" size={20} /></button>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div dir="rtl">
      <div className="bg-white p-6 mx-auto rounded-lg shadow border border-gray-200">
        <div className="flex mb-6">
          <div className="relative w-full ml-4">
            <input
              type="text"
              placeholder="ابحث عن فرد من العائلة..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 pl-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
            {renderSearchResults()}
          </div>
          <FamilyTreeDownloader members={members} />
        </div>

        {currentMember && (
          <div className="mb-4">
            <button
              onClick={handleBack}
              className="flex items-center text-primary hover:text-primary/80 mb-2"
            >
              <ArrowRight size={20} className="ml-1" />
              <span>رجوع</span>
            </button>
            {renderBreadcrumbs()}
            <h2 className="text-xl font-bold mb-4">
              أفراد عائلة {currentMember.name}
            </h2>
          </div>
        )}

        <div className="space-y-4">
          {currentMember 
            ? renderMemberList(getChildren(currentMember.id))
            : renderMemberList(rootMembers)
          }
          {(currentMember 
            ? getChildren(currentMember.id) 
            : rootMembers).length === 0 && (
            <p className="text-center text-gray-500 py-4">لا يوجد أفراد</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default FamilyNavigator;